/**
 * WHEP player: one RTCPeerConnection, recvonly video, SDP offer POSTed with the user's bearer token, answer applied,
 * DELETE on unmount. TURN credentials come from the PACS so viewers on strict networks relay through coturn.
 */
import { useEffect, useRef, useState } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { Aperture, Monitor } from "@phosphor-icons/react";
import { ConnectionDots, LinkState, NotForDiagnosis, VideoFrame } from "./ui";
import type { StreamInfo } from "./api";
import { reportStreamStats } from "./webrtc";

type State = "connecting" | "live" | "error" | "closed";

export function WhepPlayer({ stream, token }: { stream: StreamInfo; token: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<State>("connecting");
  const [detail, setDetail] = useState("");
  const [candidate, setCandidate] = useState("");

  useEffect(() => {
    let pc: RTCPeerConnection | null = null;
    let resourceUrl: string | null = null;
    let cancelled = false;
    let stopStats = () => undefined as void;
    (async () => {
      try {
        pc = new RTCPeerConnection({
          iceServers: [{ urls: stream.turn.urls, username: stream.turn.username, credential: stream.turn.credential }],
        });
        stopStats = reportStreamStats(pc, stream.whepUrl, token);
        pc.addTransceiver("video", { direction: "recvonly" });
        pc.ontrack = (ev) => {
          if (videoRef.current && ev.streams[0]) videoRef.current.srcObject = ev.streams[0];
        };
        pc.onconnectionstatechange = async () => {
          if (!pc) return;
          if (pc.connectionState === "connected") {
            setState("live");
            const stats = await pc.getStats();
            stats.forEach((r) => {
              const pair = r as RTCIceCandidatePairStats;
              if (r.type === "candidate-pair" && pair.state === "succeeded" && pair.nominated) {
                const local = stats.get(pair.localCandidateId) as { candidateType?: string } | undefined;
                setCandidate(local?.candidateType ?? "");
              }
            });
          } else if (pc.connectionState === "failed") {
            setState("error");
            setDetail("Peer connection failed. Reload the page to try again.");
          }
        };
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await new Promise<void>((resolve) => {
          if (pc!.iceGatheringState === "complete") return resolve();
          const t = setTimeout(resolve, 1500);
          pc!.onicegatheringstatechange = () => {
            if (pc!.iceGatheringState === "complete") {
              clearTimeout(t);
              resolve();
            }
          };
        });
        const res = await fetch(stream.whepUrl, {
          method: "POST",
          headers: { "Content-Type": "application/sdp", Authorization: `Bearer ${token}` },
          body: pc.localDescription!.sdp,
        });
        if (!res.ok)
          throw new Error(
            res.status === 404
              ? "no live stream from this instrument right now"
              : `WHEP ${res.status}: ${await res.text()}`
          );
        resourceUrl = res.headers.get("Location");
        const answer = await res.text();
        if (cancelled) return;
        await pc.setRemoteDescription({ type: "answer", sdp: answer });
      } catch (e) {
        setState("error");
        setDetail((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
      stopStats();
      setState("closed");
      if (resourceUrl) {
        const url = resourceUrl.startsWith("http") ? resourceUrl : new URL(resourceUrl, stream.whepUrl).toString();
        fetch(url, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }).catch(() => undefined);
      }
      pc?.close();
    };
  }, [stream.whepUrl, stream.turn.username, token]);

  const link: LinkState =
    state === "live" ? "live" : state === "error" ? "failed" : state === "closed" ? "idle" : "connecting";
  const words =
    state === "live" ? "live" : state === "error" ? "no stream" : state === "closed" ? "closed" : "connecting";
  return (
    <Stack spacing={1.25}>
      <VideoFrame aspect="16 / 9" glow={state === "live" ? "live" : "warm"}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      </VideoFrame>
      <NotForDiagnosis />
      <Stack direction="row" spacing={2} alignItems="center">
        <ConnectionDots state={link} from={<Aperture size={16} />} to={<Monitor size={16} />} label={words} />
        {candidate && (
          <Typography variant="caption" color="text.secondary">
            ICE {candidate === "relay" ? "relay (TURN)" : candidate}
          </Typography>
        )}
      </Stack>
      {state === "error" && <Alert severity="warning">{detail}</Alert>}
    </Stack>
  );
}
